import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  MessageCircle, Send, ArrowLeft, Users, Clock, Hash
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function GroupList({ groups, activeGroup, onSelect }) {
  return (
    <div className="space-y-1">
      {groups.map(g => (
        <button
          key={g.id}
          onClick={() => onSelect(g)}
          className={`w-full text-left p-3 rounded-xl transition-all ${
            activeGroup?.id === g.id ? 'bg-orange-50 border border-orange-200' : 'hover:bg-slate-50 border border-transparent'
          }`}
          data-testid={`group-${g.id}`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold ${
              g.type === 'all-stores' ? 'bg-violet-500' : g.type === 'custom' ? 'bg-teal-500' : 'bg-orange-500'
            }`}>
              {g.type === 'all-stores' ? <Users className="w-5 h-5" /> : <Hash className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-slate-900 truncate">{g.name}</p>
              {g.last_message ? (
                <p className="text-xs text-slate-400 truncate">{g.last_message.sender}: {g.last_message.content}</p>
              ) : (
                <p className="text-xs text-slate-400">No messages yet</p>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function ChatView({ group, user, onBack }) {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/messaging/groups/${group.id}/messages`);
      setMessages(res.data);
    } catch (err) {
      console.warn('Failed to fetch messages', err);
    }
  }, [group.id]);

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 5000);
    return () => clearInterval(pollRef.current);
  }, [fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/messaging/messages`, { group_id: group.id, content: newMsg.trim() });
      setNewMsg('');
      fetchMessages();
    } catch (err) {
      console.warn('Failed to send message', err);
    }
    finally { setSending(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-140px)]" data-testid="chat-view">
      {/* Chat Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
        <Button variant="ghost" size="sm" onClick={onBack} className="lg:hidden rounded-xl">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold ${
          group.type === 'all-stores' ? 'bg-violet-500' : 'bg-orange-500'
        }`}>
          {group.type === 'all-stores' ? <Users className="w-4 h-4" /> : <Hash className="w-4 h-4" />}
        </div>
        <div>
          <p className="font-bold text-sm text-slate-900">{group.name}</p>
          <p className="text-[11px] text-slate-400">{group.type === 'all-stores' ? 'All team members' : 'Store group'}</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-3">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <MessageCircle className="w-12 h-12 mb-3 text-slate-200" />
            <p className="text-sm">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map(msg => {
            const isMe = msg.sender_id === user?.id;
            return (
              <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] ${isMe ? 'order-2' : ''}`}>
                  {!isMe && <p className="text-[10px] font-semibold text-slate-500 mb-0.5 ml-1">{msg.sender_name}</p>}
                  <div className={`px-3.5 py-2.5 rounded-2xl ${
                    isMe ? 'bg-orange-500 text-white rounded-tr-md' : 'bg-slate-100 text-slate-800 rounded-tl-md'
                  }`}>
                    <p className="text-sm leading-relaxed">{msg.content}</p>
                  </div>
                  <p className={`text-[10px] mt-0.5 ${isMe ? 'text-right mr-1' : 'ml-1'} text-slate-400`}>
                    {msg.created_at ? format(new Date(msg.created_at), 'h:mm a') : ''}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="pt-3 border-t border-slate-100 flex gap-2">
        <Input
          value={newMsg}
          onChange={e => setNewMsg(e.target.value)}
          placeholder="Type a message..."
          className="rounded-xl flex-1"
          data-testid="message-input"
        />
        <Button type="submit" disabled={!newMsg.trim() || sending} className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl px-4" data-testid="send-btn">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}

export default function Messaging() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchGroups(); }, []);

  const fetchGroups = async () => {
    try { const res = await axios.get(`${API_URL}/messaging/groups`); setGroups(res.data); }
    catch {}
    finally { setLoading(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-400">Loading...</div>;
  }

  return (
    <div data-testid="messaging-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
          <MessageCircle className="w-5 h-5 text-orange-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Messages</h1>
          <p className="text-slate-500 text-sm">Group messaging across stores</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
        {/* Groups List - hide on mobile when chat active */}
        <Card className={`border border-slate-200 rounded-xl ${activeGroup ? 'hidden lg:block' : ''}`}>
          <CardContent className="p-3">
            <p className="text-xs font-semibold text-slate-400 uppercase px-3 mb-2">Groups</p>
            <GroupList groups={groups} activeGroup={activeGroup} onSelect={setActiveGroup} />
          </CardContent>
        </Card>

        {/* Chat Area */}
        <Card className={`border border-slate-200 rounded-xl ${!activeGroup ? 'hidden lg:block' : ''}`}>
          <CardContent className="p-4">
            {activeGroup ? (
              <ChatView group={activeGroup} user={user} onBack={() => setActiveGroup(null)} />
            ) : (
              <div className="flex flex-col items-center justify-center h-[400px] text-slate-400">
                <MessageCircle className="w-16 h-16 mb-4 text-slate-200" />
                <p className="text-sm font-medium">Select a group to start messaging</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
